import createFeeds from './processor';
import { getSession } from '../sessionManager';
import { SAM_ENCODER_MODEL_PATH, SAM_DECODER_MODEL_PATH, SAM_DECODER_POINTS_MODEL_PATH, SAM_STATUS } from '../config';
import postProcess from './postProcessor';
import getOpenCv from '../../utils/openCV';
import { getTensorData } from '../utils/tensorUtils';

// In-memory cache for encoder outputs, keyed by device + encoderCacheId.
// This lets us reuse expensive image embeddings across multiple decoder calls with different prompts.
const encoderCache = new Map();

/**
 * Run inference for the SAM model.
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {Array<Array<Array<number>>>} polygon
 * @param {Array<{ x: number, y: number, label: number }>} clickPoints
 * @param {Array<number>} bbox
 * @param {'webgpu'|'wasm'} [device='wasm']
 * @param {string} [encoderCacheId] - stable key to reuse encoder outputs across calls
 * @param {(step: string) => void} [onStatus] - optional progress callback (LOADING_SESSION, PREPARING, ENCODING, DECODING)
 */
export async function runInference(image, polygon, clickPoints, bbox, device = 'wasm', encoderCacheId, addingNewObjectFirstMask, onStatus) {
    const report = (step) => { try { onStatus?.(step); } catch (_) { } };
    const { cv } = await getOpenCv();
    report(SAM_STATUS.LOADING_SESSION);
    const decoderPath = addingNewObjectFirstMask ? SAM_DECODER_POINTS_MODEL_PATH : SAM_DECODER_MODEL_PATH;
    const encoderSession = await getSession('sam-encoder', SAM_ENCODER_MODEL_PATH, device);
    const decoderSession = await getSession(`sam-decoder-${addingNewObjectFirstMask ? 'points' : ''}`, decoderPath, device);

    report(SAM_STATUS.PREPARING);
    let feeds;
    feeds = createFeeds(cv, image, polygon, clickPoints, addingNewObjectFirstMask ? undefined : bbox);
    const encoderFeeds = {};
    for (const inputName of encoderSession.inputNames) {
        const feed = feeds[inputName];
        if (feed) {
            encoderFeeds[inputName] = feed;
        }
    }
    // Build a serializable snapshot of encoder feeds (for Python parity debugging).
    const serializedEncoderFeeds = {};
    for (const [name, tensor] of Object.entries(encoderFeeds)) {
        if (!tensor || typeof tensor !== 'object' || !('dims' in tensor)) continue;
        const data = getTensorData(tensor);
        serializedEncoderFeeds[name] = {
            dims: Array.isArray(tensor.dims) ? tensor.dims.slice() : [],
            data: data ? Array.from(data, (v) => Number(v)) : [],
        };
    }
    // Run (or reuse) the encoder.
    const cacheKey = encoderCacheId ? `${device}:${encoderCacheId}` : null;
    let encoderResults;
    if (cacheKey && encoderCache.has(cacheKey)) {
        // Reuse cached encoder outputs for the same image / encoderCacheId.
        encoderResults = encoderCache.get(cacheKey);
    } else {
        report(SAM_STATUS.ENCODING);
        encoderResults = await encoderSession.run(encoderFeeds);
        if (cacheKey) {
            encoderCache.set(cacheKey, encoderResults);
        }
    }
    report(SAM_STATUS.DECODING);
    const decoderFeeds = {};
    for (const inputName of decoderSession.inputNames) {
        const feed = feeds[inputName];
        if (feed) {
            decoderFeeds[inputName] = feed;
        }
    }
    const encoderOutputNames = typeof encoderResults.keys === 'function'
        ? [...encoderResults.keys()]
        : Object.keys(encoderResults);
    for (const inputName of encoderOutputNames) {
        decoderFeeds[inputName] = encoderResults[inputName];
    }
    const decoderResults = await decoderSession.run(decoderFeeds);
    const origSizeData = getTensorData(feeds.orig_im_size);
    const origImSize = origSizeData && origSizeData.length >= 2
        ? [Number(origSizeData[0]), Number(origSizeData[1])]
        : [0, 0];
    return postProcess(cv, decoderResults, origImSize);
}